// ─── Minimal Audio Button ─────────────────────────────────
// Small floating speaker icon that plays/pauses background audio.
// Auto-plays on first user interaction with volume fade-in.

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function MinimalAudioButton({
  audioSrc,
  accentColor = '#2c241d',
  bgColor = '#f8f3eb',
}) {
  const audioRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const hasAutoPlayed = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const startFadeIn = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0;
    audio.play().then(() => {
      setIsPlaying(true);
      let vol = 0;
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = setInterval(() => {
        vol = Math.min(1, vol + 0.05);
        audio.volume = vol;
        if (vol >= 1) clearInterval(fadeIntervalRef.current);
      }, 100);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onReady = () => setIsReady(true);
    audio.addEventListener('canplaythrough', onReady);
    return () => audio.removeEventListener('canplaythrough', onReady);
  }, []);

  // Auto-play on first interaction
  useEffect(() => {
    if (!isReady || hasAutoPlayed.current) return;
    const handleInteraction = () => {
      if (hasAutoPlayed.current) return;
      hasAutoPlayed.current = true;
      startFadeIn();
    };
    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });
    document.addEventListener('scroll', handleInteraction, { once: true });
    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('scroll', handleInteraction);
    };
  }, [isReady, startFadeIn]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  return (
    <>
      <audio ref={audioRef} src={audioSrc} loop preload="auto" />
      <AnimatePresence>
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1, duration: 0.4, type: 'spring' }}
          onClick={togglePlay}
          className="w-full h-full rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105 active:scale-95"
          style={{ backgroundColor: accentColor }}
          data-testid="minimal-audio-btn"
        >
          {isPlaying ? (
            <Volume2 className="w-5 h-5" style={{ color: bgColor }} />
          ) : (
            <VolumeX className="w-5 h-5" style={{ color: bgColor }} />
          )}
        </motion.button>
      </AnimatePresence>
    </>
  );
}
